// Layer 3 (Transformations): the `24 h performance` tab — pure projections
// over the seeded readings, series and engines. paul-tarp.A20 (#422), the
// `a20-meeting` stream, slice S1.
//
// Precedent copied rather than re-invented:
//   - `modules/overview/Performance.transform.ts:585` (`rowFromTriadReading`)
//     — the daily cell is `valueAt(readingId, state)`; the longer cells are
//     `windowMean(seriesId, state, from)`.
//   - `modules/overview/Performance.transform.ts:638` (`buildPlanAchievement`)
//     — the elapsed-day window headers (`Day 6 of 7`, complete production days)
//     and the `dailyPlan × elapsed days` comparator.
// Those two are the shipped idiom for exactly this table; this file is a
// third instance of it, not a new mechanism.
//
// No React, no engine mutations — every function takes state in and returns
// data out.

import {
  daysInMonth,
  diffMinutes,
  monthStart,
  productionDayOf,
  weekStart,
} from '@/versions/v1/shared/clock.derive';
import { CONCENTRATE_SPECS } from '@/versions/v1/shared/bunker-hill-concentrate-specs';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { seriesAt, valueAt, windowMean, windowSum } from '@/versions/v1/shared/series.derive';
import { juneMtdDays, juneWtdDays } from '@/versions/v1/shared/june-tonnage.derive';
import { productStreamMasses } from '@/versions/v1/modules/ingestion/periodBalance';
import { pbInZnTone } from '@/versions/v1/modules/overview/LimsTable.transform';
import { plantKpis } from '@/versions/v1/modules/maintenance/Downtime.transform';
import type { KpiTile } from '@/versions/v1/schema/plant-role';
import type { SessionState } from '@/versions/v1/schema/scenario';

// --- Windows --------------------------------------------------------------

export type PerfWindowKey = '24h' | 'wtd' | 'mtd';

export type PerfWindow = {
  key: PerfWindowKey;
  /** Group header: `24 h` · `WTD · day 6 of 7` · `MTD · day 7 of 30`. */
  label: string;
  /** Absent on `24h` — the 24 h cell is a reading, never a window. */
  from?: string;
  /** Exclusive upper bound. Set on `wtd` and `mtd`: the open production day's
   *  stamp, the first day the window excludes, so D25's complete production
   *  days bound the window (K17 round 3 / #663; K21 / #719 for WTD). Absent
   *  means the window runs to the cursor. */
  to?: string;
  /** Periods the window covers — how many days a daily figure is scaled by. */
  elapsedDays: number;
};

/** Sum of a series' measured points in `[from, to)`; an absent `to` keeps the
 *  open-ended `windowSum`. Both the WTD and MTD windows close their top
 *  (K17 r3 / D25; K21 / #719). */
function sumWindow(seriesId: string, state: SessionState, from: string, to?: string): number {
  if (!to) return windowSum(seriesId, state, from);
  return seriesAt(seriesId, state).points
    .filter(
      (p) => p.value !== null && diffMinutes(p.at, from) >= 0 && diffMinutes(p.at, to) < 0,
    )
    .reduce((total, p) => total + (p.value ?? 0), 0);
}

/** Mean of a series' measured points in `[from, to)`; an absent `to` keeps the
 *  open-ended `windowMean`. Matches `windowMean`'s zero on an empty window. */
function meanWindow(seriesId: string, state: SessionState, from: string, to?: string): number {
  if (!to) return windowMean(seriesId, state, from);
  const points = seriesAt(seriesId, state).points.filter(
    (p): p is { at: string; value: number } =>
      p.value !== null && diffMinutes(p.at, from) >= 0 && diffMinutes(p.at, to) < 0,
  );
  if (points.length === 0) return 0;
  return points.reduce((total, p) => total + p.value, 0) / points.length;
}

/** The three column groups. The 24 h column is deliberately **not** a window:
 * `rs-ore-td` stamps daily at 18:00 and the window helpers are inclusive at
 * both ends, so a cursor-minus-24h window would admit two points whenever the
 * cursor lands on a stamp (3,540 t at `2026-06-05T18:00`). Every row's 24 h
 * actual is therefore the reading itself — `valueAt`, exactly as
 * `rowFromTriadReading` treats its daily cell — and `from` stays undefined.
 * WTD's and MTD's `day N` are the **complete production-day counts** those
 * windows hold, so the header names the same days the cells sum.
 *
 * D25 / K17 round 3 (#663): the MTD window is the cursor month's **complete
 * production days** (`juneMtdDays`), the same window Metal accounting,
 * Planning and `/reports` read — not every point from the month start, which
 * counted the open 8 Jun day whole (14,121 dst over 8 days).
 *
 * D25 / K21 (#719): the WTD window is the plant week's complete production days
 * (`juneWtdDays`), the same six days Metal accounting's `wtdWindow()` and
 * `/cost` publish — not the open-ended week-start sum, which counted the open
 * 8 Jun day whole (12,372 dst over 7 days). At the demo cursor that is
 * production days 2–7 Jun, 10,652 dst against plan 6 × 1,440 = 8,640, and the
 * header reads `WTD · day 6 of 7`. */
export function windowsFor(state: SessionState): readonly PerfWindow[] {
  const mtdFrom = monthStart(state.cursor);
  const mtdDays = juneMtdDays(state);
  const lastMtdDay = mtdDays[mtdDays.length - 1];
  const wtdDays = juneWtdDays(state);
  const lastWtdDay = wtdDays[wtdDays.length - 1];
  return [
    { key: '24h', label: '24 h', elapsedDays: 1 },
    {
      key: 'wtd',
      label: `WTD · day ${wtdDays.length} of 7`,
      from: weekStart(state.cursor),
      to: lastWtdDay?.closedAt,
      elapsedDays: wtdDays.length,
    },
    {
      key: 'mtd',
      label: `MTD · day ${mtdDays.length} of ${daysInMonth(state.cursor)}`,
      from: mtdFrom,
      to: lastMtdDay?.closedAt,
      elapsedDays: mtdDays.length,
    },
  ];
}

/** The production day the cursor sits in — the "last 24 h" period, used
 * wherever a row needs a window *mean* (a head grade) even though its own
 * 24 h cell is a single reading. `productionDayOf` is the plant's own day
 * boundary (night-then-day, labelled by the day shift's date). */
function dayWindowStart(state: SessionState): string {
  return productionDayOf(state.cursor).start;
}

// --- Basis and deviation ---------------------------------------------------

export type BasisKind = 'budget' | 'design' | 'spec';

/** Basis vocabularies. A basis cell names the thing it judges against; the
 * bare word "target" names nothing until the basis is said. `design` is
 * declared for completeness — no reading on this tab carries a design
 * comparator, so it is unused on this seed. */
export const BASIS_LABEL: Record<BasisKind, string> = {
  budget: 'Budget',
  design: 'Design',
  spec: 'Spec',
};

/** Signed deviation of `actual` from `basis`, in percent of the basis —
 * modelled on `comparison-basis.ts:149` (`deviationFromBasisPct`). Declared
 * here rather than imported because that helper takes a `ComparisonBasisRead`,
 * which cannot express a spec (`readingComparisonBasis` returns `null` for
 * `rd-spec-zn-pb`) and resolves only the budget rows on this tab. */
export function deviationPct(actual: number, basis: number): number {
  if (basis === 0) return 0;
  return ((actual - basis) / basis) * 100;
}

/** `+` / `−` (U+2212), one decimal. A deviation that rounds to zero reads
 * `0.0 %` with no sign (polish.P12 adjudication 3: a signed zero is not a
 * figure a reader can use); its direction is still carried by the cell's
 * tone, which `cellTone` computes from the unrounded value. */
export function formatDeltaPct(pct: number): string {
  const magnitude = Math.abs(pct).toFixed(1);
  if (magnitude === '0.0') return '0.0 %';
  return pct < 0 ? `−${magnitude} %` : `+${magnitude} %`;
}

/** The basis cell without its kind word (`Budget 1,440` → `1,440`,
 * `Spec ≥ 67` → `≥ 67`): the measured table states the kind once, on the
 * group row (`groupBasisLabel`), rather than in every cell. */
export function basisFigure(cell: PerfCell): string {
  const word = BASIS_LABEL[cell.basisKind];
  return cell.basisLabel.startsWith(`${word} `) ? cell.basisLabel.slice(word.length + 1) : cell.basisLabel;
}

/** The group row's basis phrase — `vs budget` / `vs spec` — read from the
 * group's own cells, so a group that mixed kinds would name both. */
export function groupBasisLabel(group: PerfGroup): string {
  const kinds = [...new Set(group.rows.flatMap((row) => row.cells.map((cell) => cell.basisKind)))];
  return `vs ${kinds.map((kind) => BASIS_LABEL[kind].toLowerCase()).join(' / ')}`;
}

export type CellTone = 'ok' | 'warn' | 'bad';

/** The favourable side is `ok` whatever its size; the unfavourable side is
 * `warn` within ±2 % and `bad` beyond. `higherIsBetter === false` inverts the
 * favourable side before the magnitude test, so a genuinely negative value
 * that rounds to zero (`−0.0 %`) still tones `warn`. */
export function cellTone(pct: number, higherIsBetter: boolean): CellTone {
  const favourable = higherIsBetter ? pct >= 0 : pct <= 0;
  if (favourable) return 'ok';
  return Math.abs(pct) <= 2 ? 'warn' : 'bad';
}

// --- Rows -----------------------------------------------------------------

export const PERF_GROUPS = [
  'THROUGHPUT & PRODUCTION',
  'RECOVERY',
  'CONCENTRATE GRADES',
  'RUNTIME',
] as const;

export type PerfCell = {
  actual: number;
  basis: number;
  /** The basis cell's own words: `Budget 1,440` · `Spec ≥ 67` · `Spec ≤ 3`. */
  basisLabel: string;
  basisKind: BasisKind;
  deltaPct: number;
  tone: CellTone;
};

export type PerfRow = {
  id: string;
  group: string;
  label: string;
  unit: string;
  higherIsBetter: boolean;
  /** Decimal places the row's own seed carries — 0 for tonnage, 1 elsewhere
   * (`decimals ?? (unit === '%' ? 1 : 0)`, the sibling transform's rule). */
  decimals: number;
  cells: readonly PerfCell[];
};

export type PerfGroup = { group: string; rows: readonly PerfRow[] };

/** `1,720` · `67.0` · `3` — tabular figures, en-US grouping, the row's own
 * decimal count. */
export function formatFigure(n: number, decimals: number): string {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Tonnes ÷ (elapsed days × 24) — the feed rate is derived from the tonnage
 * row, never read from `rs-wit-021`, so the two can never disagree. */
function feedRateTph(tonnes: number, elapsedDays: number): number {
  return tonnes / (elapsedDays * 24);
}

/** The two-product budget identity, per day:
 *
 *   C_day = F_budget × h̄ ÷ c × R_budget
 *
 * `F_budget` is `rd-kpi-ore-td.target` (the Budget track), `h̄` the window's
 * mean head grade (`rs-head-pb` / `rs-head-zn`), `c` the concentrate grade
 * the product is sold at — the grade reading's `design` — and `R_budget` the
 * recovery reading's `target`. It inverts the same identity
 * `recoveryPctWithAssays` uses (`MetalAccountingLayer.transform.ts`), so the
 * budget mass reconciles with the budget recovery. No seed carries a
 * concentrate-tonnes comparator, so this is the only honest basis; the caller
 * multiplies by the window's elapsed days.
 *
 * Returns 0 when an input is missing, which the caller renders as a zero
 * basis rather than a fabricated one. */
export function concentrateBudgetTd(
  element: 'Pb' | 'Zn',
  state: SessionState,
  from: string,
  to?: string,
): number {
  const headSeries = element === 'Pb' ? 'rs-head-pb' : 'rs-head-zn';
  const gradeReadingId = element === 'Pb' ? 'rd-kpi-pb-grade' : 'rd-kpi-zn-grade';
  const recReadingId = element === 'Pb' ? 'rd-kpi-pb-rec' : 'rd-kpi-zn-rec';
  const headGrade = meanWindow(headSeries, state, from, to);
  const concGrade = readingByIdLookup(gradeReadingId)?.design;
  const recovery = readingByIdLookup(recReadingId)?.target;
  const feedBudget = readingByIdLookup('rd-kpi-ore-td')?.target;
  if (
    headGrade === 0 ||
    concGrade === undefined ||
    concGrade === 0 ||
    recovery === undefined ||
    feedBudget === undefined
  ) {
    return 0;
  }
  return ((feedBudget * headGrade) / concGrade) * (recovery / 100);
}

/** A concentrate spec threshold, read from the seed's own `gradeSpecs` — the
 * `lo` for a higher-is-better grade. Never a literal. */
function specLo(specId: 'cs-pb' | 'cs-zn', parameter: string): number {
  const spec = CONCENTRATE_SPECS.find((row) => row.id === specId);
  return spec?.gradeSpecs.find((row) => row.parameter === parameter)?.lo ?? 0;
}

/** The lower-is-better contamination spec's high side (`rd-spec-zn-pb`
 * carries `spec.hi: 3`), read from the reading rather than typed. */
function specHi(readingId: string): number {
  return readingByIdLookup(readingId)?.spec?.hi ?? 0;
}

type RowSpec = {
  id: string;
  group: string;
  label: string;
  unit: string;
  decimals: number;
  higherIsBetter: boolean;
  /** The row's 24 h actual — the reading itself, never a window. */
  actual24h: number;
  /** The row's WTD / MTD actuals. The window is passed whole so a row can use
   * its `from` for a mean and its `elapsedDays` for a scale. */
  actualWindow: (window: PerfWindow) => number;
  /** The row's comparator in one window, already scaled. */
  basis: (window: PerfWindow) => { value: number; kind: BasisKind };
  /** A hand-written basis phrase when the comparator is a spec band
   * (`Spec ≥ 67` / `Spec ≤ 3`); budget rows build theirs from the figure. */
  specLabel?: string;
  /** A row whose tone is its own rule rather than the favourable-side rule —
   * Pb in Zn reads the LIMS watch band (80 % of the smelter limit), the same
   * `pbInZnTone` the Senior card and LIMS register use, so 2.6 is amber on
   * every surface (K10c item 3). */
  tone?: (actual: number) => CellTone;
};

/** The budget comparator for a row: a daily figure scaled by the window's
 * elapsed days for a mass, or used as-is for a rate/ratio (`scaleDaily`
 * false). */
function budgetBasis(
  daily: number,
  window: PerfWindow,
  scaleDaily: boolean,
): { value: number; kind: BasisKind } {
  return { value: scaleDaily ? daily * window.elapsedDays : daily, kind: 'budget' };
}

function buildRowSpecs(state: SessionState): readonly RowSpec[] {
  const wtdFrom = weekStart(state.cursor);
  const dayFrom = dayWindowStart(state);
  const oreTarget = readingByIdLookup('rd-kpi-ore-td')?.target ?? 0;
  const dayMasses = productStreamMasses('day');
  const wtdMasses = productStreamMasses('wtd');
  const mtdMasses = productStreamMasses('mtd');
  const dayOre = valueAt('rd-kpi-ore-td', state) ?? 0;
  const dayPb = dayMasses?.C1 ?? 0;
  const dayZn = dayMasses?.C2 ?? 0;

  /** Concentrate mass per product in a window (K22 / #738). The 24 h cell is
   *  the day's own mass, unchanged; the WTD and MTD cells are
   *  `productStreamMasses(period)` — Metal accounting's own read over
   *  `wtdWindow()` / `mtdWindow()`: the head × recovery identity on the sum of
   *  the window's complete production days (D25). One product, one mass, on
   *  both routes.
   *
   *  The retired day-scaling multiplied the day's mass by the window's ore ÷
   *  the day's ore, so `/meeting` published Pb 324.2 / Zn 816.6 WTD against
   *  Metal accounting's 321.6 / 834.2, and Pb 377.5 / Zn 950.7 MTD against
   *  376.6 / 969.2. `productStreamMasses` returns `null` when the tails stream
   *  goes non-positive; the mass is then 0, never a fabricated figure. */
  const concentrateActual = (element: 'Pb' | 'Zn', window: PerfWindow): number => {
    if (window.key === '24h') return element === 'Pb' ? dayPb : dayZn;
    const masses = window.key === 'wtd' ? wtdMasses : mtdMasses;
    if (!masses) return 0;
    return element === 'Pb' ? masses.C1 : masses.C2;
  };

  /** The concentrate budget for one window: the daily identity on that
   * window's own head grade, × its elapsed days. */
  const concentrateBudget =
    (element: 'Pb' | 'Zn') =>
    (window: PerfWindow): { value: number; kind: BasisKind } =>
      budgetBasis(concentrateBudgetTd(element, state, window.from ?? dayFrom, window.to), window, true);

  /** A recovery row: the reading's live value, the window mean of its own
   * series, judged against the reading's budget target. */
  const recoveryRow = (
    id: string,
    label: string,
    readingId: string,
    seriesId: string,
  ): RowSpec => ({
    id,
    group: 'RECOVERY',
    label,
    unit: '%',
    decimals: 1,
    higherIsBetter: true,
    actual24h: valueAt(readingId, state) ?? 0,
    actualWindow: (window) => meanWindow(seriesId, state, window.from ?? wtdFrom, window.to),
    basis: () => ({ value: readingByIdLookup(readingId)?.target ?? 0, kind: 'budget' }),
  });

  return [
    {
      id: 'ore',
      group: 'THROUGHPUT & PRODUCTION',
      label: 'Tonnes milled',
      unit: 't',
      decimals: 0,
      higherIsBetter: true,
      actual24h: dayOre,
      actualWindow: (window) => sumWindow('rs-ore-td', state, window.from ?? wtdFrom, window.to),
      basis: (window) => budgetBasis(oreTarget, window, true),
    },
    {
      id: 'feed-rate',
      group: 'THROUGHPUT & PRODUCTION',
      label: 'Mill feed rate',
      unit: 't/h',
      decimals: 1,
      higherIsBetter: true,
      actual24h: feedRateTph(dayOre, 1),
      actualWindow: (window) =>
        feedRateTph(sumWindow('rs-ore-td', state, window.from ?? wtdFrom, window.to), window.elapsedDays),
      basis: () => ({ value: feedRateTph(oreTarget, 1), kind: 'budget' }),
    },
    {
      id: 'pb-con',
      group: 'THROUGHPUT & PRODUCTION',
      label: 'Pb/Ag con produced',
      unit: 't',
      decimals: 1,
      higherIsBetter: true,
      actual24h: dayPb,
      actualWindow: (window) => concentrateActual('Pb', window),
      basis: concentrateBudget('Pb'),
    },
    {
      id: 'zn-con',
      group: 'THROUGHPUT & PRODUCTION',
      label: 'Zn con produced',
      unit: 't',
      decimals: 1,
      higherIsBetter: true,
      actual24h: dayZn,
      actualWindow: (window) => concentrateActual('Zn', window),
      basis: concentrateBudget('Zn'),
    },
    recoveryRow('pb-rec', 'Pb recovery', 'rd-kpi-pb-rec', 'rs-pb-rec'),
    recoveryRow('zn-rec', 'Zn recovery', 'rd-kpi-zn-rec', 'rs-zn-rec'),
    recoveryRow('ag-rec', 'Ag recovery', 'rd-kpi-ag-rec', 'rs-ag-rec'),
    {
      id: 'pb-grade',
      group: 'CONCENTRATE GRADES',
      label: 'Pb con grade',
      unit: '%',
      decimals: 2,
      higherIsBetter: true,
      actual24h: valueAt('rd-kpi-pb-grade', state) ?? 0,
      actualWindow: (window) => meanWindow('rs-pb-grade', state, window.from ?? wtdFrom, window.to),
      basis: () => ({ value: specLo('cs-pb', 'Pb grade'), kind: 'spec' }),
      specLabel: `Spec ≥ ${formatFigure(specLo('cs-pb', 'Pb grade'), 0)}`,
    },
    {
      id: 'zn-grade',
      group: 'CONCENTRATE GRADES',
      label: 'Zn con grade',
      unit: '%',
      decimals: 2,
      higherIsBetter: true,
      actual24h: valueAt('rd-kpi-zn-grade', state) ?? 0,
      actualWindow: (window) => meanWindow('rs-zn-grade', state, window.from ?? wtdFrom, window.to),
      basis: () => ({ value: specLo('cs-zn', 'Zn grade'), kind: 'spec' }),
      specLabel: `Spec ≥ ${formatFigure(specLo('cs-zn', 'Zn grade'), 0)}`,
    },
    {
      id: 'pb-in-zn',
      group: 'CONCENTRATE GRADES',
      label: 'Pb in Zn con',
      unit: '%',
      decimals: 2,
      // The band is the spec's own high side and the reading says lower is
      // better, so the row's tone inverts on that flag.
      higherIsBetter: false,
      actual24h: valueAt('rd-spec-zn-pb', state) ?? 0,
      actualWindow: (window) => meanWindow('rs-spec-zn-pb', state, window.from ?? wtdFrom, window.to),
      basis: () => ({ value: specHi('rd-spec-zn-pb'), kind: 'spec' }),
      specLabel: `Spec ≤ ${formatFigure(specHi('rd-spec-zn-pb'), 0)}`,
      tone: pbInZnTone,
    },
    {
      id: 'mill-util',
      group: 'RUNTIME',
      label: 'Mill utilisation',
      unit: '%',
      decimals: 1,
      higherIsBetter: true,
      actual24h: valueAt('rd-kpi-mill-util', state) ?? 0,
      actualWindow: (window) => meanWindow('rs-mill-util', state, window.from ?? wtdFrom, window.to),
      basis: () => ({ value: readingByIdLookup('rd-kpi-mill-util')?.target ?? 0, kind: 'budget' }),
    },
  ];
}

/** One cell: the actual, the comparator it is judged against, and the signed
 * deviation between them toned by the row's own direction. */
function buildCell(spec: RowSpec, window: PerfWindow, actual: number): PerfCell {
  const { value, kind } = spec.basis(window);
  const pct = deviationPct(actual, value);
  const basisLabel = spec.specLabel ?? `${BASIS_LABEL[kind]} ${formatFigure(value, spec.decimals)}`;
  return {
    actual,
    basis: value,
    basisLabel,
    basisKind: kind,
    deltaPct: pct,
    tone: spec.tone ? spec.tone(actual) : cellTone(pct, spec.higherIsBetter),
  };
}

/** The 11 rows in the 4 groups, in board order. Every figure is read —
 * tonnage from `rs-ore-td`, recovery from the recovery series, grades from
 * the shift-cadence grade series, runtime from `rs-mill-util`, the two
 * concentrate masses from `productStreamMasses` (the 24 h cell the day's own
 * mass, the WTD and MTD cells the complete-day window's — K22 / #738); the
 * feed rate and the concentrate budget are derived (see their helpers). */
export function perfRows(state: SessionState): readonly PerfGroup[] {
  const windows = windowsFor(state);

  const rows: PerfRow[] = buildRowSpecs(state).map((spec) => ({
    id: spec.id,
    group: spec.group,
    label: spec.label,
    unit: spec.unit,
    higherIsBetter: spec.higherIsBetter,
    decimals: spec.decimals,
    cells: windows.map((window) =>
      buildCell(spec, window, window.key === '24h' ? spec.actual24h : spec.actualWindow(window)),
    ),
  }));

  return PERF_GROUPS.map((group) => ({
    group,
    rows: rows.filter((row) => row.group === group),
  })).filter((entry) => entry.rows.length > 0);
}

// --- KPI strip -------------------------------------------------------------

/** The tab's one KPI strip — four tiles. Ore processed and the two
 * concentrate masses compare against the same budget figures the table's 24 h
 * column does; the fourth tile is the downtime engine's own window
 * (`plantKpis(1)`, whose window ends at the engine's `BASIS_END` rather than
 * the session cursor — deliberately: the engine takes whole days and cannot
 * serve a cursor window). Every tile carries a `sub`; none ends in a delta
 * fragment, because a `sub` ending ` · +…` is split into a delta by
 * `kpiCardPropsFromTile` and this strip states its comparators in words. */
export function perfStrip(state: SessionState): readonly KpiTile[] {
  const dayFrom = dayWindowStart(state);
  const oreTarget = readingByIdLookup('rd-kpi-ore-td')?.target ?? 0;
  const oreActual = valueAt('rd-kpi-ore-td', state) ?? 0;
  const masses = productStreamMasses('day');
  const pbActual = masses?.C1 ?? 0;
  const znActual = masses?.C2 ?? 0;
  const pbBudget = concentrateBudgetTd('Pb', state, dayFrom);
  const znBudget = concentrateBudgetTd('Zn', state, dayFrom);

  const downtime = plantKpis(1);

  return [
    {
      id: 'ore-processed',
      roleId: 'superintendent',
      label: 'Ore processed',
      value: `${formatFigure(oreActual, 0)} t`,
      sub: `vs budget ${formatFigure(oreTarget, 0)} t`,
      tone: cellTone(deviationPct(oreActual, oreTarget), true),
    },
    {
      id: 'pb-ag-concentrate',
      roleId: 'superintendent',
      label: 'Pb/Ag concentrate',
      value: `${formatFigure(pbActual, 1)} t`,
      sub: `vs budget ${formatFigure(pbBudget, 1)} t`,
      tone: cellTone(deviationPct(pbActual, pbBudget), true),
    },
    {
      id: 'zn-concentrate',
      roleId: 'superintendent',
      label: 'Zn concentrate',
      value: `${formatFigure(znActual, 1)} t`,
      sub: `vs budget ${formatFigure(znBudget, 1)} t`,
      tone: cellTone(deviationPct(znActual, znBudget), true),
    },
    {
      id: 'unplanned-downtime',
      roleId: 'superintendent',
      label: 'Unplanned downtime',
      value: `${downtime.repairHours.toFixed(1)} h`,
      // A count, not a delta — see the function comment.
      sub: `${downtime.breakdownCount} breakdowns`,
      tone: downtime.repairHours > 0 ? 'warn' : 'ok',
    },
  ];
}
